import {
  DashboardWidgetError,
  DashboardWidgetLoading,
} from '@/components/custom/DashboardWidgetState';
import { StatCard } from '@/components/custom/StatCard';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAffiliatesApi } from '@/hooks/useAffiliatesApi';
import { getOwnedGymProfiles } from '@/lib/authRouting';
import { toApiError } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Building2,
  Calendar,
  CheckCircle,
  CreditCard,
  Medal,
  Trophy,
  Users,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import PayoutsPage from '../shared/PayoutsPage';
import AffiliateBilling from './AffiliateBilling';
import AffiliateEvents from './AffiliateEvents';
import AffiliateLeaderboard from './AffiliateLeaderboard';
import AffiliateMembers from './AffiliateMembers';
import AffiliateProgramming from './AffiliateProgramming';
import AffiliateTeam from './AffiliateTeam';
import MyGyms from './MyGyms';
import ScoreApprovalQueue from './ScoreApprovalQueue';
import TeamManagement from './TeamManagement';

const statusColors: Record<string, string> = {
  active: 'bg-green-500/10 text-green-500 border-green-500/20',
  pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  past_due: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
};

function AffiliateDashboardHome() {
  const affiliatesApi = useAffiliatesApi();
  const [searchParams] = useSearchParams();
  const signupSuccess = searchParams.get('signup') === 'success';

  // FEAT-APR08-003 — first-time "Add Another Gym" banner. Shows only to
  // single-gym owners, one-off per user via localStorage.
  const user = useAuthStore((s) => s.user);
  const profiles = useAuthStore((s) => s.profiles);
  const profileOwnedGymCount = getOwnedGymProfiles(profiles).length;

  // Keep portfolio visibility aligned with the server's legacy and profile-based
  // ownership sources. Profile rows remain a synchronous fallback while this
  // canonical portfolio query loads.
  const { data: portfolioData } = useQuery({
    queryKey: ['affiliate-dashboard', 'all'],
    queryFn: async () => {
      const response = await affiliatesApi.getDashboard({ scope: 'all' });
      if (!response.success) throw toApiError(response, 'Failed to load owned gyms');
      return response.data;
    },
    enabled: user?.role === 'affiliate_owner' || profileOwnedGymCount > 0,
  });
  const ownedGymCount = portfolioData?.gyms?.length ?? profileOwnedGymCount;

  // FEAT-APR08-004 — unified cross-gym scope toggle for owners with >1 gym.
  const isMultiGymOwner = ownedGymCount > 1;
  const [portfolioScope, setPortfolioScope] = useState<'current' | 'all'>('current');
  const dismissKey = user ? `dismiss_add_gym_banner_${user.id}` : null;
  const [addGymDismissed, setAddGymDismissed] = useState(() => {
    if (typeof window === 'undefined' || !dismissKey) return true;
    return window.localStorage.getItem(dismissKey) === '1';
  });
  const showAddGymBanner = ownedGymCount === 1 && !addGymDismissed;
  const dismissAddGymBanner = () => {
    if (dismissKey) window.localStorage.setItem(dismissKey, '1');
    setAddGymDismissed(true);
  };

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['affiliate-dashboard', portfolioScope],
    queryFn: async () => {
      const response = await affiliatesApi.getDashboard(
        portfolioScope === 'all' ? { scope: 'all' } : undefined
      );
      if (!response.success) throw toApiError(response, 'Failed to load dashboard data');
      return response.data;
    },
    refetchInterval: 60000,
  });

  const affiliate = data?.affiliate;
  const metrics = data?.metrics;
  const isSignupSetupPending = isLoading || !data;

  return (
    <DashboardLayout>
      {/* Signup Success Alert */}
      {signupSuccess && (
        <Alert className="mb-6 border-green-500/20 bg-green-500/10 animate-fade-in">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertTitle className="text-green-500">
            {isSignupSetupPending ? 'Payment Successful!' : 'Welcome to ErgShoot!'}
          </AlertTitle>
          <AlertDescription>
            {isSignupSetupPending
              ? 'Your payment was processed and your gym is still being activated. You can use the available shortcuts while we reconnect.'
              : 'Your gym is all set up. Start inviting members and managing your programming.'}
          </AlertDescription>
        </Alert>
      )}

      {/* FEAT-APR08-003 — multi-gym discovery banner for single-gym owners */}
      {showAddGymBanner && (
        <Alert className="mb-6 border-primary/30 bg-primary/5 animate-fade-in relative">
          <Building2 className="h-4 w-4 text-primary" />
          <AlertTitle className="text-primary">Run more than one gym?</AlertTitle>
          <AlertDescription className="pr-8">
            You can add another gym to your ErgShoot portfolio at any time. Each gym has its own
            members, programming and Stripe subscription — switch between them from the top bar.{' '}
            <Link to="/affiliate/my-gyms" className="underline font-medium">
              Open My Gyms
            </Link>
            .
          </AlertDescription>
          <button
            type="button"
            onClick={dismissAddGymBanner}
            className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}

      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">
            {affiliate?.name || 'Affiliate Dashboard'}
          </h1>
          <p className="mt-1 text-muted-foreground">Manage your gym, members, and programming.</p>
        </div>
        <div className="flex items-center gap-3">
          {/* FEAT-APR08-004 — scope toggle for multi-gym owners */}
          {isMultiGymOwner && (
            <div className="flex gap-1 rounded-md border border-border p-1">
              <Button
                size="sm"
                variant={portfolioScope === 'current' ? 'default' : 'ghost'}
                onClick={() => setPortfolioScope('current')}
              >
                This Gym
              </Button>
              <Button
                size="sm"
                variant={portfolioScope === 'all' ? 'default' : 'ghost'}
                onClick={() => setPortfolioScope('all')}
              >
                All My Gyms
              </Button>
            </div>
          )}
          {affiliate?.status && (
            <Badge variant="outline" className={statusColors[affiliate.status] || ''}>
              {affiliate.status.charAt(0).toUpperCase()}
              {affiliate.status.slice(1).replace('_', ' ')}
            </Badge>
          )}
        </div>
      </div>

      {/* Stats Grid — FEAT-APR30-001 each card now routes to a relevant view */}
      {isLoading ? (
        <DashboardWidgetLoading
          label={signupSuccess ? 'Setting up your gym…' : 'Loading gym overview…'}
        />
      ) : error ? (
        <DashboardWidgetError
          title="Gym overview unavailable"
          description="We couldn't load your latest gym metrics. Your management shortcuts are still available below."
          error={error}
          context={{
            portal: 'affiliate',
            widget: 'overview',
            endpoint: '/affiliates/dashboard',
          }}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Members"
            value={metrics?.total_members || 0}
            subtitle="Registered members"
            icon={Users}
            delay={0}
            href="/affiliate/members"
          />
          <StatCard
            title="Active Members"
            value={metrics?.active_members || 0}
            subtitle="With recent activity"
            icon={Users}
            delay={50}
            href="/affiliate/members?filter=active"
          />
          <StatCard
            title="Scores This Week"
            value={metrics?.scores_this_week || 0}
            subtitle="Logged by members"
            icon={Medal}
            delay={100}
            href="/affiliate/leaderboard"
          />
          <StatCard
            title="Subscription"
            value={affiliate?.status === 'active' ? 'Active' : 'Inactive'}
            subtitle="Billing status"
            icon={CreditCard}
            delay={150}
            href="/affiliate/billing"
          />
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card className="animate-fade-in" style={{ animationDelay: '200ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Calendar className="h-5 w-5 text-primary" />
              Programming
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              View today's workout and the full programming calendar.
            </p>
            <Link to="/affiliate/programming">
              <Button variant="outline" className="w-full">
                View Programming
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="animate-fade-in" style={{ animationDelay: '250ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Users className="h-5 w-5 text-primary" />
              Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Manage your member roster and send invitations.
            </p>
            <Link to="/affiliate/members">
              <Button variant="outline" className="w-full">
                View Members
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="animate-fade-in" style={{ animationDelay: '300ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Medal className="h-5 w-5 text-primary" />
              Leaderboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              See who's at the top and launch TV display mode.
            </p>
            <Link to="/affiliate/leaderboard">
              <Button variant="outline" className="w-full">
                View Leaderboard
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="animate-fade-in" style={{ animationDelay: '350ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Trophy className="h-5 w-5 text-primary" />
              Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Create and manage gym events and competitions.
            </p>
            <Link to="/affiliate/events">
              <Button variant="outline" className="w-full">
                View Events
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="animate-fade-in" style={{ animationDelay: '400ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <CreditCard className="h-5 w-5 text-primary" />
              Billing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Manage your subscription and payment details.
            </p>
            <Link to="/affiliate/billing">
              <Button variant="outline" className="w-full">
                Manage Billing
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

export default function AffiliateDashboard() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  const isBillingPage = location.pathname.endsWith('/billing');
  const needsPayment =
    user?.subscription_status &&
    user.subscription_status !== 'active' &&
    user.subscription_status !== 'trialing';

  if (needsPayment && !isBillingPage) {
    return <Navigate to="/affiliate/billing" replace />;
  }

  return (
    <Routes>
      <Route path="/" element={<AffiliateDashboardHome />} />
      <Route path="programming" element={<AffiliateProgramming />} />
      <Route path="team" element={<AffiliateTeam />} />
      <Route path="team/management" element={<TeamManagement />} />
      <Route path="approvals" element={<ScoreApprovalQueue />} />
      <Route path="my-gyms" element={<MyGyms />} />
      <Route path="members" element={<AffiliateMembers />} />
      <Route path="leaderboard" element={<AffiliateLeaderboard />} />
      <Route path="events" element={<AffiliateEvents />} />
      <Route
        path="billing"
        element={
          <DashboardLayout>
            <AffiliateBilling />
          </DashboardLayout>
        }
      />
      <Route path="payouts" element={<PayoutsPage />} />
    </Routes>
  );
}
